import React, { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Sparkles,
  Save,
  Trash2,
  Copy,
  Send,
  PauseCircle,
  PlayCircle,
  Image as ImageIcon,
  ShieldCheck,
  X,
  Hash,
  Plus,
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  RefreshCw,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import ImageDropzone from "@/components/ImageDropzone";
import PostPreviewCard from "@/components/PostPreviewCard";
import { StatusBadge } from "@/components/Surface";
import { PlatformChipList, platformLabel, isMultiPlatform } from "@/components/PlatformBadges";
import { toFriendlyPublishError } from "@/lib/friendlyPublishError";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";
import usePublishEligibility from "@/lib/usePublishEligibility";
import useBillingSubscription from "@/lib/useBillingSubscription";
import ConfirmDialog from "@/components/ConfirmDialog";

const PLATFORMS = ["Instagram", "Facebook", "LinkedIn", "X / Twitter"];

function isoToInputDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch (_e) {
    return "";
  }
}

function inputDateTimeToIso(value) {
  if (!value) return null;
  try {
    return new Date(value).toISOString();
  } catch (_e) {
    return null;
  }
}

export default function PostEditorDialog({ open, post, onOpenChange, onChanged, onDeleted }) {
  const { user } = useAuth();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyAction, setBusyAction] = useState(null);
  // Delete confirmation dialog state \u2014 replaces window.confirm().
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newHashtag, setNewHashtag] = useState("");

  useEffect(() => {
    if (post) {
      setDraft({
        ...post,
        cta: post.cta || "",
        hashtags: [...(post.hashtags || [])],
      });
    } else {
      setDraft(null);
    }
  }, [post]);

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const dirty = useMemo(() => {
    if (!post || !draft) return false;
    if (post.caption !== draft.caption) return true;
    if (post.cta !== (draft.cta || "")) return true;
    if (post.platform !== draft.platform) return true;
    if ((post.image_url || null) !== (draft.image_url || null)) return true;
    if ((post.scheduled_at || null) !== (draft.scheduled_at || null)) return true;
    if ((post.status || null) !== (draft.status || null)) return true;
    if (JSON.stringify(post.hashtags || []) !== JSON.stringify(draft.hashtags || [])) return true;
    return false;
  }, [post, draft]);

  // Live publish-eligibility check for the post's selected platform. We pause
  // the network call when the post is already published (there's no Publish
  // button to gate) — small optimization that keeps the dialog snappy.
  // IMPORTANT: must be called *unconditionally* before any early return so
  // React hook ordering stays stable across renders.
  const platformForEligibility = draft?.platform || null;
  const isAlreadyPublished = draft?.status === "published";
  const { eligibility: pubEligibility, refresh: refreshEligibility } = usePublishEligibility(
    platformForEligibility,
    { enabled: !!draft && !isAlreadyPublished },
  );
  // Multi-platform quota pre-check. Read-only subscription snapshot;
  // never mutates usage. Called BEFORE the early return so hook order
  // is stable across renders.
  const { sub: __editorSub } = useBillingSubscription();

  if (!draft) return null;

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = {
        caption: draft.caption,
        hashtags: draft.hashtags,
        cta: draft.cta || "",
        platform: draft.platform,
        image_url: draft.image_url,
        scheduled_at: draft.scheduled_at,
        status: draft.status,
      };
      const res = await api.patch(`/posts/${post.post_id}`, payload);
      onChanged?.(res.data);
      toast.success("Post saved");
      onOpenChange?.(false);
    } catch (_e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (kind) => {
    setBusyAction(kind);
    try {
      let res;
      if (kind === "delete") {
        // Open the polished ConfirmDialog instead of native window.confirm.
        // The actual deletion happens in `confirmDelete()` below.
        setDeleteOpen(true);
        setBusyAction(null);
        return;
      } else if (kind === "duplicate") {
        res = await api.post(`/posts/${post.post_id}/duplicate`);
        toast.success("Duplicated as draft");
        onChanged?.(res.data, { added: true });
        onOpenChange?.(false);
        return;
      } else if (kind === "approve") {
        res = await api.post(`/posts/${post.post_id}/approve`);
        toast.success("Approved and scheduled");
      } else if (kind === "reject") {
        await api.post(`/posts/${post.post_id}/reject`);
        toast.success("Rejected");
        onDeleted?.(post);
        onOpenChange?.(false);
        return;
      } else if (kind === "pause") {
        res = await api.post(`/posts/${post.post_id}/pause`);
        toast.success("Paused");
      } else if (kind === "resume") {
        res = await api.post(`/posts/${post.post_id}/resume`);
        toast.success("Resumed");
      } else if (kind === "publish") {
        res = await api.post(`/posts/${post.post_id}/publish`);
        const pdata = res?.data;
        if (pdata?.status === "published") {
          toast.success("Published");
        } else if (pdata?.status === "blocked_safety") {
          // Content Safety Gate B held this post. Never claim success.
          toast.error(
            pdata.error_message ||
              "This content can't be published because it may violate Postmoro's content safety rules. Please revise and try again.",
            { duration: 8000 },
          );
        } else if (pdata?.status === "blocked_org_policy") {
          // Phase 20 Gate C — the business's OWN opt-in content policy.
          const violations = Array.isArray(pdata.org_compliance_violations)
            ? pdata.org_compliance_violations
            : [];
          const summary = violations.length
            ? violations
                .slice(0, 3)
                .map((v) => v.message || v.rule || "policy violation")
                .join(" • ")
            : (pdata.error_message ||
                "This post was held because it may not match your organization's content policy.");
          toast.error(summary, { duration: 10000 });
        } else if (pdata?.blocked_by === "org_compliance_unavailable") {
          toast.error(
            pdata.error_message ||
              "We couldn't complete the required organization content policy check right now. Nothing was published. Please try again shortly.",
          );
        } else if (pdata?.status === "partial_published") {
          toast.warning(pdata.error_message || "Partially published — some platforms failed.");
        } else if (pdata?.status === "failed") {
          toast.error(pdata.error_message || "Publish failed");
        } else {
          // Unknown/non-terminal status — NEVER claim a false success.
          toast.info(
            pdata?.error_message || "Publish is still processing. Open the post to see its status.",
          );
        }
        refreshEligibility?.();
      } else if (kind === "regenerate-caption") {
        toast.info("Regenerating caption…");
        res = await api.post(`/posts/${post.post_id}/regenerate-caption`);
        toast.success("Caption regenerated");
      } else if (kind === "regenerate-image") {
        toast.info("Regenerating image · ~20s");
        res = await api.post(`/posts/${post.post_id}/regenerate-image`);
        toast.success("Image regenerated");
      } else if (kind === "regenerate") {
        toast.info("Regenerating post · ~25s");
        res = await api.post(`/posts/${post.post_id}/regenerate`);
        toast.success("Post regenerated");
      }
      if (res?.data) {
        setDraft({ ...res.data, hashtags: [...(res.data.hashtags || [])] });
        onChanged?.(res.data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setBusyAction(null);
    }
  };

  const addHashtag = () => {
    const t = newHashtag.trim();
    if (!t) return;
    const tag = t.startsWith("#") ? t : "#" + t;
    if (draft.hashtags.includes(tag)) {
      setNewHashtag("");
      return;
    }
    set({ hashtags: [...draft.hashtags, tag] });
    setNewHashtag("");
  };
  const removeHashtag = (i) => set({ hashtags: draft.hashtags.filter((_, idx) => idx !== i) });

  const isPending = draft.status === "pending_approval";
  const isScheduled = draft.status === "scheduled";
  const isPaused = draft.status === "paused";
  const isPublished = draft.status === "published";
  const isFailed = draft.status === "failed";

  const canPublishNow = !!pubEligibility?.can_publish;
  const pubBlockedMessage = pubEligibility && !canPublishNow ? pubEligibility.message : null;
  const pubBlockedCta = pubEligibility && !canPublishNow ? pubEligibility.cta : null;
  // Multi-platform quota derived values (hook itself is above the
  // early return so it always runs).
  const __targetsEditor = Array.isArray(post?.target_platforms) && post.target_platforms.length > 0
    ? post.target_platforms
    : (post?.platform ? [post.platform] : []);
  const __unitsEditor = Math.max(1, __targetsEditor.length);
  const __remainingEditor = __editorSub?.monthly_remaining;
  const editorQuotaBlocked =
    !isPublished
    && __unitsEditor > 1
    && __remainingEditor != null
    && __unitsEditor > __remainingEditor;
  const editorQuotaMessage = editorQuotaBlocked
    ? `You have ${__remainingEditor} post${__remainingEditor === 1 ? "" : "s"} remaining this month. Publishing to ${__targetsEditor.join(" + ")} would use ${__unitsEditor} posts. Remove one platform or upgrade to continue.`
    : null;
  const publishGateDisabled = !canPublishNow || editorQuotaBlocked;
  const publishGateTitle = editorQuotaMessage || (pubBlockedMessage || undefined);

  const provider = draft.publish_provider || null;
  const providerLabel =
    provider === "x" ? "X / Twitter" :
    provider === "facebook" ? "Facebook" :
    provider === "instagram" ? "Instagram" :
    provider === "demo" ? "Demo mode" :
    provider ? provider : null;
  const externalUrl = draft.external_post_url || null;
  const externalId = draft.external_post_id || draft.provider_post_id || null;

  // Multi-platform display awareness: a multi-platform parent post carries
  // `publish_results: { "X": {ok, external_post_url, ...}, "LinkedIn": {...} }`.
  // When present we render a per-platform delivery list INSTEAD OF the
  // singular success/failure surface so users see every target's status
  // (X live, LinkedIn live, Instagram skipped — Meta approval, etc.).
  // Legacy single-platform posts have no `publish_results` field and
  // continue to render the original surface, byte-identical to before.
  const multi = isMultiPlatform(draft);
  const publishResults = draft.publish_results || null;
  const hasPublishResults = !!publishResults && Object.keys(publishResults).length > 0;
  const showMultiDeliveryPanel = multi && hasPublishResults;

  const formatStamp = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString(undefined, {
        weekday: "short", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit",
      });
    } catch (_e) {
      return iso;
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/posts/${post.post_id}`);
      toast.success("Post deleted");
      onDeleted?.(post);
      setDeleteOpen(false);
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete post");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[color:var(--surface-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] max-w-5xl w-[95vw] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span>Post editor</span>
            <StatusBadge status={draft.status} />
            {multi ? <PlatformChipList post={draft} data-testid="editor-platforms-list" /> : null}
            {draft.source === "autopilot" ? (
              <span className="text-[10px] uppercase tracking-wider text-[color:var(--brand-secondary)]">Autopilot</span>
            ) : null}
            {provider === "demo" ? (
              <span className="text-[10px] uppercase tracking-wider text-[color:var(--accent-warm)]" data-testid="editor-demo-badge">demo</span>
            ) : null}
          </DialogTitle>
        </DialogHeader>

        {/* Multi-platform delivery panel — shows per-platform live/failed/skipped
            state for posts whose `publish_results` map is populated. This
            replaces the singular surface below to avoid the previous bug where
            an X+LinkedIn published post only showed "Live on LinkedIn". */}
        {showMultiDeliveryPanel ? (
          <div
            className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]/40 p-3 mb-2"
            data-testid="editor-multi-delivery-panel"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="text-sm font-semibold text-[color:var(--text-1)]">Delivery results</div>
              <PlatformChipList post={draft} />
            </div>
            <div className="space-y-1.5">
              {Object.entries(publishResults).map(([plat, res]) => {
                const label = platformLabel(plat);
                const isSkipped = !!res?.skipped;
                const isOk = !!res?.ok;
                const Icon = isOk ? CheckCircle2 : AlertCircle;
                const colorCls = isOk
                  ? "text-[color:var(--success)]"
                  : isSkipped
                    ? "text-[color:var(--accent-warm)]"
                    : "text-[color:var(--danger)]";
                const statusText = isOk
                  ? `Live on ${label}`
                  : isSkipped
                    ? `${label}: Skipped`
                    : `${label}: Failed`;
                // Friendly mapping — only for failed rows. Raw provider
                // string is preserved as a `title` tooltip on the copy
                // block so operators/admins can still read it. Success
                // and skip rows keep the existing raw text (they use
                // `error_message` to carry the skip reason and are
                // already accurate).
                const rowFriendly = (!isOk && !isSkipped && res?.error_message)
                  ? toFriendlyPublishError(label, res.error_message)
                  : null;
                return (
                  <div
                    key={plat}
                    className="flex items-start gap-2 text-xs"
                    data-testid={`editor-delivery-row-${label.toLowerCase()}`}
                  >
                    <Icon className={"h-3.5 w-3.5 mt-0.5 shrink-0 " + colorCls} />
                    <div className="flex-1 min-w-0">
                      <div className={"font-semibold " + colorCls}>
                        {rowFriendly ? rowFriendly.title : statusText}
                      </div>
                      {res?.error_message ? (
                        <div
                          className="text-[11px] text-[color:var(--text-2)] mt-0.5 leading-snug break-words"
                          title={rowFriendly ? res.error_message : undefined}
                          data-testid={`editor-delivery-error-${label.toLowerCase()}`}
                        >
                          {rowFriendly ? rowFriendly.explanation : res.error_message}
                        </div>
                      ) : null}
                      {res?.external_post_id ? (
                        <div className="text-[10px] text-[color:var(--text-3)] mt-0.5 font-mono break-all">
                          ID: {res.external_post_id}
                        </div>
                      ) : null}
                    </div>
                    {res?.external_post_url ? (
                      <a
                        href={res.external_post_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] hover:bg-[color:var(--surface-3)] text-[color:var(--text-1)] px-2 py-1 shrink-0"
                        data-testid={`editor-delivery-link-${label.toLowerCase()}`}
                      >
                        <ExternalLink className="h-3 w-3" />
                        View live
                      </a>
                    ) : null}
                  </div>
                );
              })}
            </div>
            {/* Retry button still useful if at least one delivery failed */}
            {(draft.status === "failed" || draft.status === "partial_published") ? (
              <div className="mt-3">
                <Button
                  size="sm"
                  onClick={() => runAction("publish")}
                  disabled={busyAction === "publish" || !canPublishNow}
                  title={!canPublishNow && pubBlockedMessage ? pubBlockedMessage : undefined}
                  className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="editor-retry-publish-multi"
                >
                  {busyAction === "publish" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                  Retry publish
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Truthful publishing telemetry - failure surface. Uses the
            shared friendlyPublishError mapper so this heading + copy
            match PostCard's failed-post card. Raw provider text is
            still surfaced verbatim as a title-attribute tooltip on
            the copy block (developer/admin diagnostic path). */}
        {!showMultiDeliveryPanel && isFailed ? (() => {
          const friendly = toFriendlyPublishError(providerLabel, draft.error_message);
          return (
          <div
            className="rounded-md border border-[color:rgba(255,92,108,0.35)] bg-[color:rgba(255,92,108,0.06)] p-3 mb-2"
            data-testid="editor-failure-surface"
          >
            <div className="flex items-start gap-2 flex-wrap">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-[color:var(--danger)]" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[color:var(--danger)]">
                  {friendly.title}
                </div>
                {draft.error_message ? (
                  <div
                    className="text-xs text-[color:var(--text-2)] mt-1 leading-relaxed break-words"
                    title={friendly.matched ? draft.error_message : undefined}
                    data-testid="editor-error-message"
                  >
                    {friendly.explanation}
                  </div>
                ) : null}
                <div className="text-[11px] text-[color:var(--text-3)] mt-1 flex items-center gap-3 flex-wrap">
                  {draft.failed_at ? <span>Failed at: {formatStamp(draft.failed_at)}</span> : null}
                  {draft.attempt_count ? <span>Attempts: {draft.attempt_count}</span> : null}
                  {draft.platform_response_status ? <span>HTTP {draft.platform_response_status}</span> : null}
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => runAction("publish")}
                disabled={busyAction === "publish" || !canPublishNow}
                title={!canPublishNow && pubBlockedMessage ? pubBlockedMessage : undefined}
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="editor-retry-publish"
              >
                {busyAction === "publish" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                Retry publish
              </Button>
            </div>
          </div>
          );
        })() : null}

        {/* Truthful publishing telemetry - success surface */}
        {!showMultiDeliveryPanel && isPublished ? (
          <div
            className="rounded-md border border-[color:rgba(47,231,161,0.35)] bg-[color:rgba(47,231,161,0.06)] p-3 mb-2"
            data-testid="editor-published-surface"
          >
            <div className="flex items-start gap-2 flex-wrap">
              <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-[color:var(--success)]" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[color:var(--success)]">
                  {providerLabel ? `Live on ${providerLabel}` : "Published"}
                </div>
                <div className="text-[11px] text-[color:var(--text-3)] mt-1 flex items-center gap-3 flex-wrap">
                  {draft.published_at ? <span>Published: {formatStamp(draft.published_at)}</span> : null}
                  {externalId ? (
                    <span className="font-mono break-all">
                      ID: <span className="text-[color:var(--text-1)]">{externalId}</span>
                    </span>
                  ) : null}
                </div>
              </div>
              {externalUrl ? (
                <a
                  href={externalUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] hover:bg-[color:var(--surface-3)] text-[color:var(--text-1)] px-2.5 py-1.5"
                  data-testid="editor-external-link"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> View live
                </a>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Left: image */}
          <div className="lg:col-span-4 space-y-3">
            <ImageDropzone value={draft.image_url} onChange={(v) => set({ image_url: v })} label="Square image" testidPrefix="editor-image" />
            <Button
              type="button"
              variant="outline"
              className="w-full border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
              onClick={() => runAction("regenerate-image")}
              disabled={busyAction === "regenerate-image"}
              data-testid="editor-regen-image"
            >
              {busyAction === "regenerate-image" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ImageIcon className="h-4 w-4 mr-2" />}
              Regenerate image with AI
            </Button>
            <div className="hairline" />
            <div className="eyebrow">Live preview</div>
            <PostPreviewCard
              post={{
                caption: draft.caption || "Your caption preview…",
                cta: draft.cta,
                image_url: draft.image_url,
                platform: draft.platform,
                hashtags: draft.hashtags || [],
                scheduled_at: draft.scheduled_at,
                published_at: draft.published_at,
                published_page_name: draft.published_page_name,
              }}
              pageName={draft.published_page_name || user?.business_name || "Your Business"}
              handle={user?.business_name ? user.business_name.toLowerCase().replace(/\s+/g, "_") : "your_business"}
            />
          </div>

          {/* Right: editor */}
          <div className="lg:col-span-8 space-y-4">
            <Tabs defaultValue="content" className="w-full">
              <TabsList className="bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)]">
                <TabsTrigger value="content" data-testid="editor-tab-content">Content</TabsTrigger>
                <TabsTrigger value="schedule" data-testid="editor-tab-schedule">Schedule</TabsTrigger>
                <TabsTrigger value="meta" data-testid="editor-tab-meta">Platform</TabsTrigger>
              </TabsList>

              <TabsContent value="content" className="mt-3 space-y-3">
                <div>
                  <label className="text-xs text-[color:var(--text-2)]">Caption</label>
                  <Textarea
                    value={draft.caption || ""}
                    onChange={(e) => set({ caption: e.target.value })}
                    className="mt-1 min-h-[160px] bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                    data-testid="editor-caption"
                  />
                </div>
                <div>
                  <label className="text-xs text-[color:var(--text-2)]">Call to action</label>
                  <Input
                    value={draft.cta || ""}
                    onChange={(e) => set({ cta: e.target.value })}
                    placeholder="e.g. Tap the link in bio"
                    className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                    data-testid="editor-cta"
                  />
                </div>
                <div>
                  <label className="text-xs text-[color:var(--text-2)] flex items-center gap-2">
                    <Hash className="h-3.5 w-3.5" /> Hashtags
                  </label>
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid="editor-hashtags">
                    {draft.hashtags.map((h, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-[11px] rounded-full border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)] px-2 py-0.5" data-testid="editor-hashtag-chip">
                        {h}
                        <button type="button" onClick={() => removeHashtag(i)} className="text-[color:var(--text-3)] hover:text-[color:var(--danger)]" aria-label={`Remove ${h}`} data-testid={`editor-hashtag-remove-${i}`}>
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                    {draft.hashtags.length === 0 ? <span className="text-xs text-[color:var(--text-3)]">No hashtags yet.</span> : null}
                  </div>
                  <div className="mt-2 flex items-center gap-2 max-w-sm">
                    <Input
                      value={newHashtag}
                      onChange={(e) => setNewHashtag(e.target.value)}
                      placeholder="newHashtag"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addHashtag();
                        }
                      }}
                      className="bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                      data-testid="editor-hashtag-input"
                    />
                    <Button type="button" variant="outline" onClick={addHashtag} className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]" data-testid="editor-hashtag-add">
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
                    onClick={() => runAction("regenerate-caption")}
                    disabled={busyAction === "regenerate-caption"}
                    data-testid="editor-regen-caption"
                  >
                    {busyAction === "regenerate-caption" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                    Regenerate caption
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
                    onClick={() => runAction("regenerate")}
                    disabled={busyAction === "regenerate"}
                    data-testid="editor-regen-all"
                  >
                    {busyAction === "regenerate" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                    Regenerate everything
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="schedule" className="mt-3 space-y-3">
                <div>
                  <label className="text-xs text-[color:var(--text-2)]">Status</label>
                  <Select value={draft.status} onValueChange={(v) => set({ status: v })}>
                    <SelectTrigger className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]" data-testid="editor-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="pending_approval">Pending approval</SelectItem>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="paused">Paused</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-[color:var(--text-2)]">Scheduled date & time</label>
                  <Input
                    type="datetime-local"
                    value={isoToInputDateTime(draft.scheduled_at)}
                    onChange={(e) => set({ scheduled_at: inputDateTimeToIso(e.target.value) })}
                    className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                    data-testid="editor-schedule"
                  />
                  <div className="text-[11px] text-[color:var(--text-3)] mt-1">
                    Save to apply the new schedule. Status auto-switches to Scheduled if you set a future time and leave status as Draft.
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="meta" className="mt-3 space-y-3">
                <div>
                  <label className="text-xs text-[color:var(--text-2)]">Platform</label>
                  <Select value={draft.platform} onValueChange={(v) => set({ platform: v })}>
                    <SelectTrigger className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]" data-testid="editor-platform">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PLATFORMS.map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>
            </Tabs>

            <div className="hairline" />
            {/* Multi-platform quota pre-check banner (rendered even when the
                per-platform eligibility check is green — a two-platform
                publish against a one-post remaining quota needs its own
                explanation). Backend still enforces the same rule at 402. */}
            {editorQuotaBlocked ? (
              <div
                className="rounded-md border p-3 text-xs flex items-start gap-2 flex-wrap border-[color:rgba(255,92,108,0.35)] bg-[color:rgba(255,92,108,0.06)] text-[color:var(--danger)]"
                data-testid="editor-quota-block"
                role="alert"
              >
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0 text-[color:var(--text-2)] leading-relaxed">
                  {editorQuotaMessage}
                </div>
                <Link to="/billing" className="shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
                    data-testid="editor-quota-block-cta"
                  >
                    Upgrade
                  </Button>
                </Link>
              </div>
            ) : null}
            {/* Publish readiness banner (connection / Meta review / monthly limit) */}
            {!isPublished && pubEligibility && !canPublishNow && pubBlockedMessage ? (
              <div
                className={
                  "rounded-md border p-3 text-xs flex items-start gap-2 flex-wrap " +
                  (pubEligibility.limit_reached
                    ? "border-[color:rgba(255,92,108,0.35)] bg-[color:rgba(255,92,108,0.06)] text-[color:var(--danger)]"
                    : "border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.06)] text-[color:var(--accent-warm)]")
                }
                data-testid="editor-publish-blocked"
              >
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0 text-[color:var(--text-2)] leading-relaxed">
                  {pubBlockedMessage}
                </div>
                {pubBlockedCta ? (
                  <Link to={pubBlockedCta.href} className="shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
                      data-testid="editor-publish-blocked-cta"
                    >
                      <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                      {pubBlockedCta.label}
                    </Button>
                  </Link>
                ) : null}
              </div>
            ) : null}

            {/* Quick lifecycle actions */}
            <div className="flex flex-wrap gap-2">
              {isPending ? (
                <>
                  <Button onClick={() => runAction("approve")} disabled={busyAction === "approve"} className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]" data-testid="editor-approve">
                    {busyAction === "approve" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                    Approve
                  </Button>
                  <Button onClick={() => runAction("reject")} disabled={busyAction === "reject"} variant="outline" className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)]" data-testid="editor-reject">
                    Reject
                  </Button>
                </>
              ) : null}
              {isScheduled ? (
                <Button onClick={() => runAction("pause")} disabled={busyAction === "pause"} variant="outline" className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]" data-testid="editor-pause">
                  {busyAction === "pause" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PauseCircle className="h-4 w-4 mr-2" />}
                  Pause
                </Button>
              ) : null}
              {isPaused ? (
                <Button onClick={() => runAction("resume")} disabled={busyAction === "resume"} variant="outline" className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]" data-testid="editor-resume">
                  {busyAction === "resume" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-2" />}
                  Resume
                </Button>
              ) : null}
              {!isPublished ? (
                <Button
                  onClick={() => runAction("publish")}
                  disabled={busyAction === "publish" || publishGateDisabled}
                  title={publishGateTitle}
                  variant="outline"
                  className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)] disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="editor-publish"
                >
                  {busyAction === "publish" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                  Publish now
                </Button>
              ) : null}
              <Button onClick={() => runAction("duplicate")} disabled={busyAction === "duplicate"} variant="outline" className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]" data-testid="editor-duplicate">
                {busyAction === "duplicate" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Copy className="h-4 w-4 mr-2" />}
                Duplicate
              </Button>
              <Button onClick={() => runAction("delete")} disabled={busyAction === "delete"} variant="ghost" className="text-[color:var(--danger)] hover:text-[color:var(--danger)] hover:bg-[color:rgba(255,92,108,0.08)] ml-auto" data-testid="editor-delete">
                {busyAction === "delete" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Delete
              </Button>
            </div>

            <div className="hairline" />
            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange?.(false)} data-testid="editor-cancel">
                Close
              </Button>
              <Button
                onClick={onSave}
                disabled={saving || !dirty}
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold disabled:opacity-50"
                data-testid="editor-save"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                {dirty ? "Save changes" : "No changes"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete post?"
        description="This will remove the post from your dashboard. This action cannot be undone."
        secondaryNote={
          isPublished
            ? "This will not remove the post from the social platform. It will only remove it from Postmoro. The post will still count toward this month\u2019s published usage."
            : null
        }
        confirmLabel="Delete post"
        cancelLabel="Cancel"
        destructive
        loading={deleting}
        onConfirm={confirmDelete}
        testidPrefix="editor-delete"
      />
    </Dialog>
  );
}
